import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Button, Collapsible, Presence } from '@chakra-ui/react';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { PulsatingText } from '@src/components/sco/Matching/UserSearchLoadingState';
import { useDelayedText } from '@src/components/sco/TypingText';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import useOnScreen from '@src/hooks/useOnScreen';
import { gql } from '@src/lib/gql';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useRef, useState } from 'react';
type UserMatchExplanationEffect = 'none' | 'typing' | 'fade' | 'collapse';
export const UserMatchExplanation = props => {
  const $ = _c(8);
  const onScreenRef = useRef(null);
  const isOnScreen = useOnScreen(onScreenRef);
  const [show, setShow] = useState(!!props.preloadedUserMatchExplanation);
  useBreakpointContext();
  let t0;
  let t1;
  if ($[0] !== isOnScreen) {
    t0 = () => {
      if (isOnScreen) {
        setShow(true);
      }
    };
    t1 = [isOnScreen];
    $[0] = isOnScreen;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== props.effect || $[4] !== props.preloadedUserMatchExplanation || $[5] !== props.userMatchId || $[6] !== show) {
    t2 = <Box ref={onScreenRef} width="full">{!show ? <Button size="sm" variant="outline" onClick={() => setShow(true)}>See Why You Matched</Button> : <Presence animationDuration="moderate" present={true} animationName={{
        _open: "fade-in",
        _closed: "fade-out"
      }}><ShowExplanation effect={props.effect} preloadedMatchExplanation={props.preloadedUserMatchExplanation} userMatchId={props.userMatchId} /></Presence>}</Box>;
    $[3] = props.effect;
    $[4] = props.preloadedUserMatchExplanation;
    $[5] = props.userMatchId;
    $[6] = show;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const ShowExplanation = props => {
  const $ = _c(35);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t0;
  if ($[0] !== getBreakpointValue) {
    t0 = getBreakpointValue(["sm", "sm", "md"]);
    $[0] = getBreakpointValue;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      color: "colorPalette.fg",
      fontSize: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      bgColor: "colorPalette.fg/10",
      borderRadius: "sm",
      paddingX: "0.25em",
      paddingY: "0.15em",
      color: "colorPalette.fg"
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1) {
    t3 = {
      p: t1,
      strong: t2
    };
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const markdownTypographyStyles = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = gql`
      query ($userMatchId: String!, $generateIfEmpty: Boolean) {
        scoUserMatch(userMatchId: $userMatchId) {
          id
          matchExplanation(generateIfEmpty: $generateIfEmpty)
        }
      }
    `;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = !!props.preloadedMatchExplanation;
  let t6;
  if ($[8] !== props.userMatchId) {
    t6 = {
      userMatchId: props.userMatchId,
      generateIfEmpty: true
    };
    $[8] = props.userMatchId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = {
      ssr: false,
      skip: t5,
      variables: t6
    };
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const query = useQuery(t4, t7);
  const userMatch = query.data?.scoUserMatch;
  const matchExplanation = props.preloadedMatchExplanation || userMatch?.matchExplanation || "";
  const debouncedMatchExplanation = useDebounce(matchExplanation || false, 50);
  const delayedMatchExplanation = useDelayedText(matchExplanation, 15, matchExplanation && props.effect === "typing");
  if (!matchExplanation) {
    let t8;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Box paddingY="1em"><PulsatingText isActive={true}>Thinking...</PulsatingText></Box>;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  if (props.effect === "collapse") {
    const t8 = !!debouncedMatchExplanation;
    let t9;
    if ($[14] !== debouncedMatchExplanation || $[15] !== markdownTypographyStyles) {
      t9 = <Collapsible.Content><MarkdownRenderer typographyStyles={markdownTypographyStyles}>{debouncedMatchExplanation}</MarkdownRenderer></Collapsible.Content>;
      $[14] = debouncedMatchExplanation;
      $[15] = markdownTypographyStyles;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== props.effectDelay || $[18] !== t8 || $[19] !== t9) {
      t10 = <Collapsible.Root delay={props.effectDelay} open={t8}>{t9}</Collapsible.Root>;
      $[17] = props.effectDelay;
      $[18] = t8;
      $[19] = t9;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    return t10;
  }
  if (props.effect === "fade") {
    const t8 = !!debouncedMatchExplanation;
    let t9;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        _open: "fade-in",
        _closed: "fade-out"
      };
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== debouncedMatchExplanation || $[23] !== markdownTypographyStyles) {
      t10 = <MarkdownRenderer typographyStyles={markdownTypographyStyles}>{debouncedMatchExplanation}</MarkdownRenderer>;
      $[22] = debouncedMatchExplanation;
      $[23] = markdownTypographyStyles;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== props.effectDelay || $[26] !== t10 || $[27] !== t8) {
      t11 = <Presence animationDuration="moderate" delay={props.effectDelay} present={t8} animationName={t9}>{t10}</Presence>;
      $[25] = props.effectDelay;
      $[26] = t10;
      $[27] = t8;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    return t11;
  }
  if (props.effect === "typing") {
    let t8;
    if ($[29] !== delayedMatchExplanation || $[30] !== markdownTypographyStyles) {
      t8 = <MarkdownRenderer typographyStyles={markdownTypographyStyles}>{delayedMatchExplanation}</MarkdownRenderer>;
      $[29] = delayedMatchExplanation;
      $[30] = markdownTypographyStyles;
      $[31] = t8;
    } else {
      t8 = $[31];
    }
    return t8;
  }
  let t8;
  if ($[32] !== markdownTypographyStyles || $[33] !== matchExplanation) {
    t8 = <MarkdownRenderer typographyStyles={markdownTypographyStyles}>{matchExplanation}</MarkdownRenderer>;
    $[32] = markdownTypographyStyles;
    $[33] = matchExplanation;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  return t8;
};